import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { automationRuleAPI } from '../services/api';
import { groupClientLetterTemplates } from '../utils/clientLetterTemplateGroups';
import ContentHeader from '../components/ContentHeader';
import DeleteIconButton from '../components/DeleteIconButton';
import './AutomationRules.css';

const EVENT_OPTIONS = [
  { value: 'milestone.completed_date_set', label: 'Milestone: completion date set (was empty)' },
];

const ACTION_OPTIONS = [
  { value: 'email_suggest', label: 'Suggest client email (template group)' },
  { value: 'notify_internal', label: 'Notify assigned SC (inbox)' },
];

const MILESTONE_TYPE_HINTS = [
  'SCHOOL',
  'TPC',
  'IFSP_INITIAL',
  'INTAKE',
  'EXIT',
  'TPM',
  'ASQ',
  'IFSP_6MO_1',
  'IFSP_12MO_1',
  'IFSP_6MO_2',
  'IFSP_12MO_2',
  'IFSP_6MO_3',
];

function AutomationRules() {
  /** Page is super-admin–only (route + API); no separate tenant permission needed to edit rules here. */
  const canManage = true;
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [letterGroups, setLetterGroups] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const [form, setForm] = useState({
    milestone_type: '',
    action_type: 'email_suggest',
    template_base: '',
    display_label: '',
    category: 'Client Letters',
    title_template: '{{clientFullName}} — {{milestoneName}} completed',
    body_template: 'Completion date: {{completedDate}}. Referral: {{referralId}}',
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await automationRuleAPI.list();
      setRules(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load automation rules');
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setTemplatesLoading(true);
        const res = await automationRuleAPI.getClientLetterTemplates();
        const list = res.data?.data || [];
        if (!cancelled) setLetterGroups(groupClientLetterTemplates(Array.isArray(list) ? list : []));
      } catch {
        if (!cancelled) setLetterGroups([]);
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const groupedBaseCodes = useMemo(() => new Set(letterGroups.map((g) => g.baseCode)), [letterGroups]);
  const selectTemplateValue =
    !form.template_base
      ? ''
      : groupedBaseCodes.has(form.template_base)
        ? form.template_base
        : '__custom__';

  const onCreate = async (e) => {
    e.preventDefault();
    if (!canManage) return;
    const milestone_type = form.milestone_type.trim() || null;
    const action_type = form.action_type;
    let action_payload = {};
    if (action_type === 'email_suggest') {
      const template_base = form.template_base.trim();
      if (!template_base) {
        setError('Template base code is required for email suggestions.');
        return;
      }
      action_payload = {
        template_base,
        category: form.category.trim() || 'Client Letters',
        ...(form.display_label.trim() ? { display_label: form.display_label.trim() } : {}),
      };
    } else {
      const title_template = form.title_template.trim();
      if (!title_template) {
        setError('Title is required for inbox notifications.');
        return;
      }
      action_payload = {
        title_template,
        body_template: form.body_template.trim() || '',
      };
    }

    try {
      setSaving(true);
      setError(null);
      await automationRuleAPI.create({
        event_key: 'milestone.completed_date_set',
        transition: 'null_to_date',
        milestone_type,
        action_type,
        action_payload,
        enabled: true,
        sort_order: 0,
      });
      setForm((f) => ({
        ...f,
        milestone_type: '',
        template_base: '',
        display_label: '',
      }));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create rule');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!canManage || !window.confirm('Delete this automation rule?')) return;
    try {
      setError(null);
      await automationRuleAPI.delete(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete');
    }
  };

  const onToggle = async (rule) => {
    if (!canManage) return;
    try {
      setError(null);
      await automationRuleAPI.update(rule.id, { enabled: !rule.enabled });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update');
    }
  };

  return (
    <div className="automation-rules-page">
      <ContentHeader title="Automation rules (super admin)" />
      <p className="automation-rules-intro">
        Super-admin only. Rules apply to this tenant; tenant admins and coordinators do not see this screen.
      </p>
      <p className="automation-rules-intro">
        When a milestone&apos;s <strong>completion date</strong> is set for the first time, rules here can suggest a client
        email template or notify the client&apos;s <strong>assigned service coordinator</strong> in the work inbox.
        Placeholders for inbox text: <code>{'{{clientFirstName}}'}</code>, <code>{'{{clientLastName}}'}</code>,{' '}
        <code>{'{{clientFullName}}'}</code>, <code>{'{{milestoneName}}'}</code>, <code>{'{{milestoneType}}'}</code>,{' '}
        <code>{'{{completedDate}}'}</code>, <code>{'{{referralId}}'}</code>, <code>{'{{clientId}}'}</code>.
      </p>
      <p className="automation-rules-intro">
        For <strong>email suggestions</strong>, choose the same <strong>letter</strong> you see on the client&apos;s{' '}
        <strong>Client Email</strong> page. Behind the scenes we store the template <em>base code</em>: the part of the
        database code that is shared by English and Spanish (for example, if codes are <code>my-letter-english</code> and{' '}
        <code>my-letter-spanish</code>, the base is <code>my-letter</code> — not the friendly display name, and not the
        full <code>-english</code> suffix).
      </p>

      {error && <p className="automation-rules-error" role="alert">{error}</p>}
      {loading && <p className="automation-rules-loading">Loading…</p>}

      {!loading && (
        <table className="automation-rules-table">
          <thead>
            <tr>
              <th>On</th>
              <th>Milestone type</th>
              <th>Action</th>
              <th>Details</th>
              {canManage && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr>
                <td colSpan={canManage ? 5 : 4} className="automation-rules-empty">
                  No rules yet. Add one below.
                </td>
              </tr>
            )}
            {rules.map((r) => (
              <tr key={r.id}>
                <td>
                  <button
                    type="button"
                    className="automation-rules-toggle"
                    onClick={() => onToggle(r)}
                    disabled={!canManage}
                    aria-pressed={r.enabled}
                  >
                    {r.enabled ? 'Yes' : 'No'}
                  </button>
                </td>
                <td>{r.milestone_type || <em>any</em>}</td>
                <td>{r.action_type}</td>
                <td>
                  <pre className="automation-rules-payload">
                    {JSON.stringify(r.action_payload, null, 0)}
                  </pre>
                </td>
                {canManage && (
                  <td>
                    <DeleteIconButton
                      aria-label="Delete rule"
                      onClick={() => onDelete(r.id)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canManage && (
        <form className="automation-rules-form" onSubmit={onCreate}>
          <h2 className="automation-rules-form-title">Add rule</h2>
          <label className="automation-rules-field">
            <span>Milestone type (blank = all types)</span>
            <input
              name="milestone_type"
              list="milestone-type-hints"
              value={form.milestone_type}
              onChange={(e) => setForm((f) => ({ ...f, milestone_type: e.target.value }))}
              placeholder="e.g. SCHOOL"
            />
            <datalist id="milestone-type-hints">
              {MILESTONE_TYPE_HINTS.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </label>

          <label className="automation-rules-field">
            <span>Action</span>
            <select
              value={form.action_type}
              onChange={(e) => setForm((f) => ({ ...f, action_type: e.target.value }))}
            >
              {ACTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          <input type="hidden" name="event" value={EVENT_OPTIONS[0].value} readOnly />

          {form.action_type === 'email_suggest' ? (
            <>
              <label className="automation-rules-field">
                <span>Client letter (same list as Client Email)</span>
                {templatesLoading && (
                  <span className="automation-rules-templates-loading"> Loading templates…</span>
                )}
                {letterGroups.length > 0 ? (
                  <>
                    <select
                      value={selectTemplateValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '__custom__') {
                          setForm((f) => ({ ...f, template_base: '' }));
                        } else if (v === '') {
                          setForm((f) => ({ ...f, template_base: '' }));
                        } else {
                          setForm((f) => ({ ...f, template_base: v }));
                        }
                      }}
                      aria-describedby="automation-template-base-hint"
                    >
                      <option value="">Select a letter…</option>
                      {letterGroups.map((g) => (
                        <option key={g.baseCode} value={g.baseCode}>
                          {g.displayName || g.baseCode} ({g.baseCode})
                        </option>
                      ))}
                      <option value="__custom__">Other — type base code manually…</option>
                    </select>
                    <p id="automation-template-base-hint" className="automation-rules-field-hint">
                      Stored value in parentheses matches how Client Email groups English/Spanish pairs.
                    </p>
                  </>
                ) : (
                  !templatesLoading && (
                    <p className="automation-rules-field-hint">
                      No templates with category &quot;Client Letters&quot; yet. Add them under Settings → Email Templates,
                      or enter a base code below if your letters use another category.
                    </p>
                  )
                )}
                {(selectTemplateValue === '__custom__' || letterGroups.length === 0) && !templatesLoading && (
                  <input
                    className="automation-rules-custom-base"
                    value={form.template_base}
                    onChange={(e) => setForm((f) => ({ ...f, template_base: e.target.value }))}
                    placeholder="Base code only (e.g. my-letter, not my-letter-english)"
                    aria-label="Template base code"
                  />
                )}
              </label>
              <label className="automation-rules-field">
                <span>Optional label (shown in tracking prompt)</span>
                <input
                  value={form.display_label}
                  onChange={(e) => setForm((f) => ({ ...f, display_label: e.target.value }))}
                />
              </label>
              <label className="automation-rules-field">
                <span>Template category</span>
                <input
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                />
              </label>
            </>
          ) : (
            <>
              <label className="automation-rules-field">
                <span>Inbox title</span>
                <input
                  value={form.title_template}
                  onChange={(e) => setForm((f) => ({ ...f, title_template: e.target.value }))}
                  required
                />
              </label>
              <label className="automation-rules-field">
                <span>Inbox body (optional)</span>
                <textarea
                  value={form.body_template}
                  onChange={(e) => setForm((f) => ({ ...f, body_template: e.target.value }))}
                  rows={4}
                />
              </label>
            </>
          )}

          <button type="submit" className="automation-rules-submit" disabled={saving}>
            {saving ? 'Saving…' : 'Add rule'}
          </button>
        </form>
      )}

    </div>
  );
}

export default AutomationRules;
